import { useState } from "react";
import { toast } from "sonner";
import { Mail, Clock, Instagram } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.ok);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t.contact.err);
    }
    setSending(false);
  };

  return (
    <div className="py-14 lg:py-20 pb-24" data-testid="contact-page">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
        <Reveal>
          <p className="eyebrow mb-3">MIORA</p>
          <h1 className="font-serif font-light text-5xl lg:text-6xl text-ink">{t.contact.title}</h1>
          <p className="mt-4 text-ink/60 text-sm max-w-md">{t.contact.sub}</p>
        </Reveal>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-14 mt-14 items-start">
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="field-label" htmlFor="contact-field-name">{t.contact.name}</label>
                <input id="contact-field-name" required value={form.name} onChange={set("name")} className="field" data-testid="contact-field-name" />
              </div>
              <div>
                <label className="field-label" htmlFor="contact-field-email">{t.contact.email}</label>
                <input id="contact-field-email" type="email" required value={form.email} onChange={set("email")} className="field" data-testid="contact-field-email" />
              </div>
            </div>
            <div>
              <label className="field-label" htmlFor="contact-field-subject">{t.contact.subject}</label>
              <input id="contact-field-subject" value={form.subject} onChange={set("subject")} className="field" data-testid="contact-field-subject" />
            </div>
            <div>
              <label className="field-label" htmlFor="contact-field-message">{t.contact.message}</label>
              <textarea id="contact-field-message" required rows={6} value={form.message} onChange={set("message")} className="field resize-none" data-testid="contact-field-message" />
            </div>
            <button type="submit" disabled={sending} className="btn btn-ink" data-testid="contact-send-button">
              {sending ? "…" : t.contact.send}
            </button>
          </form>

          <aside className="bg-cream border border-ink/10 rounded-sm p-8 space-y-8 lg:sticky lg:top-28" data-testid="contact-info">
            <div>
              <p className="eyebrow mb-3">{t.contact.csTitle}</p>
              <p className="flex items-center gap-3 text-sm text-ink/75">
                <Mail className="w-4 h-4 text-rose-deep" strokeWidth={1.5} />
                <span data-testid="contact-email">{t.contact.emailLabel}</span>
              </p>
              <p className="flex items-center gap-3 text-sm text-ink/75 mt-3">
                <Clock className="w-4 h-4 text-rose-deep" strokeWidth={1.5} />
                <span data-testid="contact-hours">
                  {t.contact.hoursValue} <span className="text-ink/45 text-[12px]">{t.contact.responseNote}</span>
                </span>
              </p>
            </div>
            <div>
              <p className="eyebrow mb-3">{t.contact.socialLabel}</p>
              <div className="space-y-2.5">
                <a href="https://instagram.com/mioraboutique_" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm text-ink/75 hover:text-rose-deep transition-colors" data-testid="contact-instagram">
                  <Instagram className="w-4 h-4 text-rose-deep" strokeWidth={1.5} />
                  Instagram — @mioraboutique_
                </a>
                <a href="https://tiktok.com/@mioraboutique_" target="_blank" rel="noreferrer" className="block text-sm text-ink/75 hover:text-rose-deep transition-colors ps-7" data-testid="contact-tiktok">
                  TikTok — @mioraboutique_
                </a>
                <a href="https://snapchat.com/add/mioraboutqiue" target="_blank" rel="noreferrer" className="block text-sm text-ink/75 hover:text-rose-deep transition-colors ps-7" data-testid="contact-snapchat">
                  Snapchat — mioraboutqiue
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
